import { FormEvent, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import { APP_NAME, APP_TAGLINE } from "../constants/brand";
import { requestPasswordReset, confirmPasswordReset } from "../services/api";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailParam = searchParams.get("email") ?? "";
  const tokenParam = searchParams.get("token") ?? "";

  const hasToken = Boolean(emailParam && tokenParam);

  const [email, setEmail] = useState(emailParam);
  const [token] = useState(tokenParam);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleRequestReset(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const result = await requestPasswordReset(email);
      setNotice(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore durante la richiesta");
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmReset(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (newPassword !== confirmPassword) {
      setError("Le password non corrispondono.");
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset({ email, token, new_password: newPassword });
      setNotice("Password reimpostata con successo. Puoi ora effettuare il login.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore durante il reset");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="auth-screen">
      <div className="auth-panel">
        <div className="auth-brand">
          <img src="/incassaflow-logo.svg" alt={APP_NAME} className="auth-logo" />
          <div>
            <h1>{APP_NAME}</h1>
            <p>{APP_TAGLINE}</p>
          </div>
        </div>

        <h2 style={{ textAlign: "center", fontSize: "1.15rem", margin: "0 0 8px" }}>
          {hasToken ? "Nuova password" : "Recupera password"}
        </h2>

        {hasToken ? (
          <form onSubmit={handleConfirmReset} className="form-grid">
            <label>
              Nuova password
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </label>
            <label>
              Conferma password
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
            </label>

            {error && <p className="error-text">{error}</p>}
            {notice && (
              <div className="guide-box">
                <p className="success-text" style={{ margin: 0 }}>{notice}</p>
                <button type="button" className="ghost-btn" onClick={() => navigate("/auth")}>
                  Vai al login
                </button>
              </div>
            )}

            {!notice && (
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Attendere..." : "Reimposta password"}
              </button>
            )}
          </form>
        ) : (
          <form onSubmit={handleRequestReset} className="form-grid">
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            {error && <p className="error-text">{error}</p>}
            {notice && <p className="success-text">{notice}</p>}

            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? "Attendere..." : "Invia link di recupero"}
            </button>

            <button type="button" className="ghost-btn" onClick={() => navigate("/auth")}>
              Torna al login
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
